import { getCellEditor, getCellEditorParams } from "./cellEditorHandler";
import { cellRenderer, cellStyle } from "./gridCellHandlers";
import { Checkbox, Radio } from "@mui/material";
import CustomFloatingFilterfrom from "./CustomFloatingFilter";
import { useSelector, useDispatch } from "react-redux";
import { setSelectedRows, toggleStatusChangeRow } from "../../../redux/reducers/manageProviderSlice";
import { isFieldMatch } from "../../../utils/utils";
import { useState } from "react";

const getColDisplayLabel = (label = '') => {
  if (/^DynamicCols\./i.test(label)) return label.split('.').pop();
  return label;
};

const DisabledHeaderCheckbox = () => (
  <input type="checkbox" disabled style={{ cursor: "not-allowed" }} />
);

const CustomCheckboxRenderer = (props) => {
  const dispatch = useDispatch();
  const selectedRows = useSelector(state => state.manageProvider.selectedRows || []);
  const isChecked = selectedRows.includes(props.data.provider_Id);

  const handleChange = (e) => {
    // Update your selection state in Redux or component state
    dispatch(setSelectedRows({
      providerId: props.data.provider_Id,
      checked: e.target.checked,
      rowData: props.data,
    }));
  };

  return (
    <input
      type="checkbox"
      checked={isChecked}
      onChange={handleChange}
      onClick={e => e.stopPropagation()} // Prevent AgGrid row click
    />
  );
};

const checkboxColumn = {
  headerName: "",
  field: "custom_checkbox",
  width: 50,
  minWidth: 50,
  maxWidth: 50,
  editable: false,
  pinned: "left",
  suppressMenu: true,
  menuTabs: [],
  cellRenderer: CustomCheckboxRenderer,
  headerComponent: DisabledHeaderCheckbox,
  cellStyle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};

const ActivateCheckboxRenderer = (props) => {
  const dispatch = useDispatch();
  const selectedRows = useSelector(state => state.manageProvider.statusChangeSelectedRows || []);
  const isChecked = selectedRows.some(r => r.npi === props.data?.npi);

  const handleChange = () => {
    dispatch(toggleStatusChangeRow(props.data));
  };

  return (
    <Checkbox
      checked={isChecked}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
      size="small"
      sx={{
        padding: 0,
        "&.Mui-checked": {
          color: "#FF8200",
        },
        "& .MuiSvgIcon-root": {
          fontSize: 16,
          strokeWidth: 0.5,
          stroke: "currentColor",
        },
        color: "rgba(0,0,0,0.38)",
      }}
    />
  );
};

const StatusChangeHeaderComponent = () => (
  <span style={{ fontSize: "11px", fontWeight: 600, color: "#555" }}>Status</span>
);

const activateCheckboxColumn = {
  headerName: "Status",
  field: "activate_checkbox",
  width: 100,
  minWidth: 100,
  maxWidth: 100,
  editable: false,
  pinned: "left",
  suppressMenu: true,
  menuTabs: [],
  cellRenderer: ActivateCheckboxRenderer,
  headerComponent: StatusChangeHeaderComponent,
  cellStyle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};

export const generateColumns = ({
  headers = [],
  deleteEnabled,
  statusChangeEnabled,
  showSearchFields,
  providerActiveStatus = "active",
  propertyMappings = {}
}) => {
  return [
    ...(deleteEnabled ? [checkboxColumn] : []),
    ...(statusChangeEnabled ? [activateCheckboxColumn] : []),
    ...headers.map((header) => ({
      headerName: getColDisplayLabel(header?.label || ""),
      wrapText: header.key[0] === 'groupEntity', // Only wrapText for groupEntity
      autoHeight: header.key[0] === 'groupEntity', // Only autoHeight for groupEntity
      headerTooltip: header?.label && header?.label.length > 5 ? getColDisplayLabel(header?.label) : null,
      field: Array.isArray(header?.key) ? header.key[0] : header?.key || "",
      sortable: header?.sortable ?? false,
      filter: header?.filterable,
      floatingFilter: showSearchFields,
      floatingFilterComponent: CustomFloatingFilterfrom,
      suppressFloatingFilterButton: { suppressFilterButton: true, debounceMs: 300, immediateRefresh: true },
      minWidth: header?.min_width || 140,
      cellStyle: (params) => {
        const baseStyle = {
          display: 'flex', // Use flexbox for centering
          alignItems: 'center', // Vertically center content
          justifyContent: 'flex-start', // Align content to the left
          // padding: '4px',
          height: '100%', // Ensure the cell takes the full row height
          whiteSpace: 'nowrap', // Prevent wrapping for all columns
          overflow: 'hidden', // Hide overflow text
          textOverflow: 'ellipsis', // Add ellipsis for long text
          // paddingLeft: '8px !important',
          // paddingRight: '8px !important'
        };

        // Allow wrapping and normal behavior only for groupEntity
        if (header.key[0] === 'groupEntity') {
          return {
            ...baseStyle,
            whiteSpace: 'normal', // Allow wrapping for groupEntity
            alignItems: 'flex-start', // Align to top for multi-line content
          };
        }
        // Highlight cells changed via dialog interaction
        let highlightStyle = {};
        try {
          const fieldKey = (Array.isArray(header.key) ? header.key[0] : header.key || '').toLowerCase();
          const matchesField = (f) => isFieldMatch(f.field_name || "", fieldKey, params.context?.pdmDynamicSchema);

          // 1. Check local _dialogChanged injection (immediate feedback)
          if (params?.data?._dialogChanged && Array.isArray(params.data._dialogChanged)) {
            if (params.data._dialogChanged.some(k => {
                const kLower = (k || '').toLowerCase();
                return isFieldMatch(kLower, fieldKey, params.context?.pdmDynamicSchema);
            })) {
              highlightStyle = { backgroundColor: '#FFF3CD' };
            }
          }

          // 2. Check global pdmGridChanges from context (persistence after refresh)
          if (!highlightStyle.backgroundColor && params.context?.pdmGridChanges) {
            const npi = params.node?.data?.npi;
            const changeEntry = params.context.pdmGridChanges.find(entry => entry.npi === npi);
            if (changeEntry?.fields?.some(matchesField)) {
              highlightStyle = { backgroundColor: '#FFF3CD' };
            }
          }
        } catch { }

        return {
          ...baseStyle,
          ...cellStyle(params), // existing logic
          ...highlightStyle,
        };
      },
      cellRenderer,
      tooltipValueGetter: () =>
        header?.groupable ? "Click For More Details" : null,
      headerComponentParams: {
        template: `
          <div class="ag-cell-label-container" role="presentation">
                      <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
            <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
            <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
              <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
              ${header?.mandatory
            ? ' <span style="color: #A70B00;">*</span>'
            : ""
          }
                            <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
              <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
              <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
                            <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
              <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
            </div>
          </div>`
      },
      cellEditor: getCellEditor(header),
      cellEditorParams: getCellEditorParams(header),
      valueGetter: (params) => {
        try {
          if (!params.data || !header?.key) return '';
          const headerField = Array.isArray(header.key) ? header.key[0] : header.key;

          // Normalize keys for robust matching: remove non-alphanumerics and compare lowercase
          const normalize = (s) => (s || '').toString().replace(/[^a-z0-9]/gi, '').toLowerCase();
          const expected = normalize(headerField);
          const actualDataKey = Object.keys(params.data || {}).find(k => normalize(k) === expected);
          const value = actualDataKey ? params.data[actualDataKey] : null;
          if (value === undefined || value === null) return '';

          if (headerField.toLowerCase() === 'license' && !Array.isArray(value)) {
            const licenseTypes = ['Medical', 'CDS', 'DEA'];
            for (const type of licenseTypes) {
              if (value[type] && value[type].length > 0) {
                return value[type][0]?.licenseNumber || '';
              }
            }
            return '';
          }

          if (Array.isArray(value)) {
            if (value.length === 0) return '';

            const selectBestField = (obj) => {
              if (!obj || typeof obj !== 'object') return '';

              const keys = Object.keys(obj);
              if (keys.length === 0) return '';

              const priorityPatterns = [
                /name$/i,
                /^name$/i,
              ];

              for (const pattern of priorityPatterns) {
                const matchingKey = keys.find(key => pattern.test(key));
                if (matchingKey && typeof obj[matchingKey] === 'string' && obj[matchingKey].trim() !== '') {
                  return obj[matchingKey];
                }
              }

              for (const key of keys) {
                if (typeof obj[key] === 'string' && obj[key].trim() !== '') {
                  return obj[key];
                }
              }

              return '';
            };

            return selectBestField(value[0]);
          }

          if (typeof value === 'object' && value !== null) {
            const keys = Object.keys(value);
            for (const key of keys) {
              if (typeof value[key] === 'string' && value[key].trim() !== '') {
                return value[key];
              }
            }
            return '';
          }

          return value;
        } catch (e) {
          console.error('[PDM-GRID] valueGetter error for field', params?.colDef?.field, e);
          return '';
        }
      },
      ...header,
      editable: providerActiveStatus === "inactive" ? false : header?.editable,
    })),
  ];
};
